import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { useGoalCompletion } from "@/lib/GoalCompletionContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Trash2, Plus, AlertCircle } from "lucide-react";

const GOAL_LABELS = {
  weight: "Target Weight",
  body_fat: "Body Fat %",
  chest: "Chest",
  waist: "Waist",
  hips: "Hips",
  arms: "Arms",
  thighs: "Thighs",
};

const GOAL_UNITS = {
  weight: "kg",
  body_fat: "%",
  chest: "cm",
  waist: "cm",
  hips: "cm",
  arms: "cm",
  thighs: "cm",
};

export default function FitnessGoals() {
  const { user } = useAuth();
  const { setCompletedGoal } = useGoalCompletion();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ goal_type: "weight", target_value: "", current_value: "" });
  const [error, setError] = useState("");

  const { data: goals = [] } = useQuery({
    queryKey: ["fitness_goals"],
    queryFn: () => base44.entities.FitnessGoal.filter({ is_active: true }, "-created_date", 50),
    enabled: !!user,
  });

  // Auto-complete goals at 100%
  useEffect(() => {
    goals.forEach((goal) => {
      const progress = getProgress(goal);
      if (progress >= 100 && goal.is_active) {
        completeMutation.mutate({ id: goal.id, goalType: goal.goal_type });
      }
    });
  }, [goals]);

  const existingGoalTypes = goals.map(g => g.goal_type);

  const createMutation = useMutation({
    mutationFn: () => {
      if (existingGoalTypes.includes(formData.goal_type)) {
        throw new Error(`You already have an active ${GOAL_LABELS[formData.goal_type]} goal.`);
      }
      return base44.entities.FitnessGoal.create({
        goal_type: formData.goal_type,
        target_value: parseFloat(formData.target_value),
        current_value: parseFloat(formData.current_value) || undefined,
        unit: GOAL_UNITS[formData.goal_type],
        is_active: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fitness_goals"] });
      setShowForm(false);
      setFormData({ goal_type: "weight", target_value: "", current_value: "" });
      setError("");
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.FitnessGoal.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["fitness_goals"] }),
  });

  const completeMutation = useMutation({
    mutationFn: ({ id }) => base44.entities.FitnessGoal.update(id, { is_active: false }),
    onSuccess: (_, { goalType }) => {
      setCompletedGoal(goalType);
      queryClient.invalidateQueries({ queryKey: ["fitness_goals"] });
    },
    onError: (error) => {
      console.error("Error completing goal:", error);
    },
  });

  const getProgress = (goal) => {
    if (!goal.current_value || goal.current_value === 0) return 0;
    const progress = Math.abs(goal.target_value - (goal.current_value || 0));
    const total = Math.abs(goal.target_value);
    return Math.max(0, Math.min(100, ((total - progress) / total) * 100));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Fitness Goals</h2>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowForm(true)}
          className="gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Goal
        </Button>
      </div>

      {goals.length === 0 ? (
        <Card className="p-6 border-0 shadow-sm text-center">
          <p className="text-sm text-muted-foreground">No fitness goals yet. Create one to get started!</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {goals.map((goal) => (
            <Card key={goal.id} className="p-4 border-0 shadow-sm">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="text-sm font-medium">{GOAL_LABELS[goal.goal_type]}</p>
                  <p className="text-xs text-muted-foreground">
                    Target: {goal.target_value} {goal.unit}
                    {goal.current_value ? ` • Current: ${goal.current_value}` : ""}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => deleteMutation.mutate(goal.id)}
                  className="text-destructive hover:text-destructive/80"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <Progress value={getProgress(goal)} className="h-2" />
              <p className="text-xs text-muted-foreground mt-2">{Math.round(getProgress(goal))}% progress</p>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Set Fitness Goal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {error && (
              <Alert className="border-destructive/50 bg-destructive/10">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <AlertDescription className="text-destructive">{error}</AlertDescription>
              </Alert>
            )}
            <div>
              <Label className="text-xs">Goal Type</Label>
              <Select value={formData.goal_type} onValueChange={(val) => { setFormData({ ...formData, goal_type: val }); setError(""); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(GOAL_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key} disabled={existingGoalTypes.includes(key)}>
                      {label} {existingGoalTypes.includes(key) ? "(active)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Target Value ({GOAL_UNITS[formData.goal_type]})</Label>
              <Input
                type="number"
                placeholder="80"
                value={formData.target_value}
                onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Current Value (optional)</Label>
              <Input
                type="number"
                placeholder="85"
                value={formData.current_value}
                onChange={(e) => setFormData({ ...formData, current_value: e.target.value })}
              />
            </div>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!formData.target_value || createMutation.isPending || existingGoalTypes.includes(formData.goal_type)}
              className="w-full"
            >
              {createMutation.isPending ? "Creating..." : "Create Goal"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}